import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';

import type { ExistingControlMatch } from '../../models/import.model';
import { compositeCoverageForSelection } from '../../utils/import-preview.utils';
import { SkeletonDirective } from '../../shared/directives/skeleton.directive';
import { ControlRefComponent } from '../control-ref/control-ref';
import { matchFrameworkIdentity } from '../../utils/framework.utils';

/**
 * "Reuse existing or create new?" panel for composite-control creation.
 *
 * Shared UX for two surfaces:
 *   - AI-import assistant's composite preview overlay
 *   - CreateControlModal's composite Details step (manual composite)
 *
 * The panel is presentation-only: parent owns the wire (
 * ``reuseControlIds`` + ``createGapFiller`` become
 * ``ControlCreateRequest.reuse_control_ids`` /
 * ``.create_gap_filler`` for the manual path, and
 * ``link_to_existing_control_ids`` / ``create_new_for_unmatched_components``
 * for the import preview PUT). Same shape both ways — see
 * ``composite_persister.persist_reuse_and_gap_filler`` on the BE.
 *
 * Coverage math is delegated to ``compositeCoverageForSelection()`` so
 * the panel, the CreateControlModal pick-step gate, and the import
 * assistant's reuse-chip view-model share ONE derivation.
 */
interface CompositeReuseCommit {
  readonly reuseControlIds: readonly string[];
  readonly createGapFiller: boolean;
}

@Component({
  selector: 'app-composite-reuse-panel',
  imports: [MatIconModule, TranslocoModule, SkeletonDirective, ControlRefComponent],
  templateUrl: './composite-reuse-panel.html',
  styleUrl: './composite-reuse-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompositeReusePanelComponent {
  /** Component-template refs the composite is meant to cover — e.g.
   *  ``['A.5.18', 'A.8.2', 'A.8.3']``. Order-preserved. */
  readonly componentRefs = input<readonly string[]>([]);
  /** Raw refs the BE couldn't resolve to a template row — surface as
   *  "still needs mapping" chips. Import-preview only; the manual
   *  composite path never has these. */
  readonly unresolvedRefs = input<readonly string[]>([]);
  /** Live-control candidates that could cover any of the components.
   *  Deduped by ``control_id``. Parent supplies (fetched via
   *  ``composite_resolver.find_existing_controls_for_templates`` on
   *  the BE for the manual path, or from the preview-node envelope
   *  for the import path). */
  readonly candidates = input<readonly ExistingControlMatch[]>([]);
  /** True while the parent is fetching candidates. When set, the panel
   *  renders a compact loading affordance in place of the empty-hint
   *  or candidate list. The gap-filler section stays visible because
   *  its state (toggle + copy) is independent of candidate discovery. */
  readonly candidatesLoading = input<boolean>(false);
  /** Non-null when the parent's candidate fetch failed. The panel
   *  renders an error hint + optional retry chip in place of the
   *  empty / coverage sections so the user isn't misled into
   *  thinking there are no duplicates when we simply couldn't
   *  check. Null = no error. */
  readonly candidatesError = input<string | null>(null);
  /** Read-only mode — e.g. an already-committed reuse decision the
   *  user is reviewing. Buttons hidden; checkboxes disabled. */
  readonly readOnly = input<boolean>(false);
  /** Whether to render the sticky action bar ("Save" / "Create new only"
   *  buttons). Set to false when the panel is embedded inside a larger
   *  wizard whose own Next / Cancel buttons drive commit — e.g. inline
   *  inside CreateControlModal's composite Details step. The parent
   *  reads ``reuseControlIds`` + ``createGapFiller`` from the two-way
   *  models on save regardless. Defaults ``true`` so the standalone
   *  overlay use-case (import assistant) works out of the box. */
  readonly showActions = input<boolean>(true);

  /** Live control ids the user has picked to reuse. Two-way binding
   *  so the parent can seed initial selection (from the preview-node
   *  or from prior modal state) and receive updates. */
  readonly reuseControlIds = model<readonly string[]>([]);
  /** Companion intent flag — when the reuse selection doesn't cover
   *  every component, this toggles whether a new gap-filler control
   *  is materialised for the uncovered refs. */
  readonly createGapFiller = model<boolean>(true);

  /** Fires when the user commits the current selection. Parent decides
   *  what "commit" means (persist a preview-node PUT for import, close
   *  the panel + include fields in the next ControlCreateRequest for
   *  manual). */
  readonly saved = output<CompositeReuseCommit>();
  /** Shortcut — clear selection + gap-filler flag and emit. Signals
   *  "ignore reuse, just create the whole composite from scratch". */
  readonly createNewOnly = output<CompositeReuseCommit>();
  /** Fires when the user clicks the "try again" chip in the error
   *  hint. Parent re-issues the candidate fetch. */
  readonly retryRequested = output<void>();

  protected onRetryClick(): void {
    this.retryRequested.emit();
  }

  /** Coverage derivation. Delegates to the shared wrapper so panel +
   *  CreateControlModal pick-step gate + import-assistant reuse-chip
   *  share ONE derivation. */
  protected readonly coverage = computed(() =>
    compositeCoverageForSelection(
      this.componentRefs(),
      this.candidates(),
      this.reuseControlIds(),
      this.unresolvedRefs()
    )
  );

  protected readonly reusedIdSet = computed(() => new Set(this.reuseControlIds()));
  /** Framework identity resolver reachable from the template so the
   *  candidate ref chip can render qualified without a view-model layer. */
  protected readonly matchFrameworkIdentity = matchFrameworkIdentity;

  /** Skeleton candidate-row count. Fixed at 3 — enough rows to make
   *  the loading state's footprint match a typical populated panel
   *  without over-reserving on small candidate sets. */
  protected readonly skeletonRows = computed(() => Array.from({ length: 3 }));

  /** Semantic intent for the coverage summary — drives the icon +
   *  colour tone shown on the summary line. Same four states the
   *  import-assistant reuse overlay uses so the visual language is
   *  aligned across both surfaces (see IA_HTML#ia-reuse-overlay-
   *  coverage-summary). Ordered by check chain so no state overlaps:
   *   - ``create-new`` — no reuse picks; whole composite is minted fresh
   *   - ``full``      — every component covered by a reuse pick
   *   - ``hybrid``    — some reuse + gap-filler will mint the rest
   *   - ``warning``   — some reuse but gaps unaddressed (no gap-filler)
   */
  protected readonly coverageIntent = computed<'create-new' | 'full' | 'hybrid' | 'warning'>(() => {
    const cov = this.coverage();
    const reusedCount = cov.reusedIds.length;
    const gapCount = cov.uncoveredCount;
    if (reusedCount === 0) return 'create-new';
    if (gapCount === 0) return 'full';
    if (this.createGapFiller()) return 'hybrid';
    return 'warning';
  });

  /** Icon glyph matching ``coverageIntent()``. */
  protected readonly coverageIcon = computed<string>(() => {
    switch (this.coverageIntent()) {
      case 'create-new':
        return 'add_circle_outline';
      case 'full':
        return 'check_circle';
      case 'hybrid':
        return 'call_split';
      case 'warning':
        return 'warning';
    }
  });

  /** Fixed-length iterable driving the coverage-meter tick loop. */
  protected coverageTicks(n: number): readonly 0[] {
    return new Array(Math.max(0, n)).fill(0);
  }

  /** i18n branch selectors — the repo doesn't have @jsverse/transloco-messageformat
   *  installed, so ICU plural strings render raw. We keep singular/plural
   *  as separate keys and pick between them here. Interpolation still uses
   *  the standard ``{{ var }}`` Transloco pipe. */
  protected uncoveredWarningKey(): string {
    return this.coverage().uncoveredCount === 1
      ? 'composite_reuse_panel.uncovered_one'
      : 'composite_reuse_panel.uncovered_other';
  }

  protected candidateCoversKey(count: number): string {
    return count === 1
      ? 'composite_reuse_panel.candidate_covers_one'
      : 'composite_reuse_panel.candidate_covers_other';
  }

  protected gapDescriptionKey(): string {
    const uncovered = this.coverage().uncoveredCount;
    if (uncovered === 0) return 'composite_reuse_panel.gap_toggle_desc_none';
    if (uncovered === 1) return 'composite_reuse_panel.gap_toggle_desc_one';
    return 'composite_reuse_panel.gap_toggle_desc_other';
  }

  protected readonly canSave = computed(() => {
    if (this.readOnly()) return false;
    const cov = this.coverage();
    // At least one action must be committing something — either a
    // reuse pick OR a gap-filler create. Both empty is a no-op that
    // shouldn't be reachable as a save.
    return this.reuseControlIds().length > 0 || (this.createGapFiller() && cov.uncoveredCount > 0);
  });

  protected onToggleCandidate(id: string, checked: boolean): void {
    if (this.readOnly()) return;
    const current = new Set(this.reuseControlIds());
    if (checked) current.add(id);
    else current.delete(id);
    this.reuseControlIds.set(Array.from(current));
  }

  protected onToggleGapFiller(checked: boolean): void {
    if (this.readOnly()) return;
    this.createGapFiller.set(checked);
  }

  protected onSaveClick(): void {
    this.saved.emit({
      reuseControlIds: this.reuseControlIds(),
      createGapFiller: this.createGapFiller(),
    });
  }

  protected onCreateNewOnlyClick(): void {
    // "Create new only" = ignore all reuse picks + create a fresh
    // composite from every component (BE materialises Composite with
    // template_id=None + junction rows for every component).
    this.reuseControlIds.set([]);
    this.createGapFiller.set(true);
    this.createNewOnly.emit({ reuseControlIds: [], createGapFiller: true });
  }
}
